import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The owl at a wait (`OwlWait`) and at a result (`OwlMoment`), for React.
 *
 * This file stays tiny and is all an app's main chunk carries: the drawing
 * (`engine.ts`) is loaded with `import()` inside an effect, never
 * `React.lazy`, so nothing ever suspends — not even a Suspense fallback with
 * no boundary above it. A failed load, a missing 2D canvas or a drawing error
 * all end in the host's own `fallback`, and none of it can reload the page.
 *
 * Both are decoration: the canvas is `aria-hidden`, they never hold a result
 * back, and the words around them don't change.
 *
 * Plan: docs/plans/2026-10-22-owl-waits-everywhere.md (OwlWait, OwlMoment)
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './motion.js';
export { prefersReducedMotion };
let engineLoad = null;
/** Start loading the drawing early (a screen that is about to wait). */
export function preloadOwl() {
    if (!engineLoad) {
        engineLoad = import('./engine.js');
        // A failed load is forgotten, so the next owl tries again.
        engineLoad.catch(() => {
            engineLoad = null;
        });
    }
    return engineLoad.catch(() => null);
}
/** Shown before this, nothing: a wait that ends sooner never shows the owl. */
export const OWL_REVEAL_MS = 400;
/** The owl fades in over this. */
export const OWL_FADE_MS = 150;
/** A moment that hasn't drawn by then keeps its fallback for good, so a late
 *  engine never replays an old result. */
export const MOMENT_DEADLINE_MS = 500;
const VISUALLY_HIDDEN = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};
function boxStyle(size, style) {
    return { position: 'relative', display: 'inline-block', width: size, height: size, flex: 'none', ...style };
}
function canvasStyle(visible, reduced) {
    return {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: visible ? 1 : 0,
        transition: reduced ? undefined : `opacity ${OWL_FADE_MS}ms ease-out`,
    };
}
const CENTRED = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };
/** The owl at a wait in progress. Mount it when the wait starts and unmount it
 *  when the wait ends. */
export function OwlWait({ kind, size = 48, plant = false, fallback = null, label, onEnd, className, style }) {
    const canvasRef = useRef(null);
    const handle = useRef(null);
    const [revealed, setRevealed] = useState(false);
    // A moment after the reveal: the engine has had time to draw, so a missing
    // owl means the fallback (no spinner flash before a quick owl).
    const [graceOver, setGraceOver] = useState(false);
    const [live, setLive] = useState(false);
    const [failed, setFailed] = useState(false);
    const [reduced] = useState(prefersReducedMotion);
    const report = useRef({ start: Date.now(), shown: false, act: null, kind, onEnd });
    // The report keeps the kind the wait began with, like `act` (its first);
    // the owl itself follows the current one.
    report.current.onEnd = onEnd;
    const kindNow = useRef(kind);
    kindNow.current = kind;
    useEffect(() => {
        void preloadOwl();
        const r = report.current;
        r.start = Date.now();
        const timer = setTimeout(() => setRevealed(true), OWL_REVEAL_MS);
        const grace = setTimeout(() => setGraceOver(true), OWL_REVEAL_MS + OWL_FADE_MS);
        return () => {
            clearTimeout(timer);
            clearTimeout(grace);
            try {
                r.onEnd?.({ kind: r.kind, elapsedMs: Date.now() - r.start, shown: r.shown, act: r.act });
            }
            catch {
                // telemetry never breaks a page
            }
        };
    }, []);
    useEffect(() => {
        if (!revealed)
            return;
        let cancelled = false;
        void preloadOwl().then((engine) => {
            const canvas = canvasRef.current;
            if (cancelled || !canvas)
                return;
            if (!engine) {
                setFailed(true);
                return;
            }
            try {
                handle.current = engine.mountWait(canvas, {
                    kind: kindNow.current,
                    plant,
                    reducedMotion: reduced,
                    onAct: (id) => {
                        report.current.act ??= id;
                    },
                    onLive: (on) => {
                        if (on)
                            report.current.shown = true;
                        if (!cancelled)
                            setLive(on);
                    },
                    onDead: () => {
                        if (!cancelled)
                            setFailed(true);
                    },
                });
            }
            catch {
                setFailed(true);
            }
        });
        return () => {
            cancelled = true;
            handle.current?.stop();
            handle.current = null;
        };
        // `plant` and `reduced` are fixed for the owl's life.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revealed]);
    useEffect(() => {
        handle.current?.setKind(kind);
    }, [kind]);
    const showFallback = !live && (failed || graceOver);
    return (_jsxs("span", { className: className, style: boxStyle(size, style), children: [label && (_jsx("span", { role: "status", style: VISUALLY_HIDDEN, children: label })), !failed && (_jsx("canvas", { ref: canvasRef, "aria-hidden": "true", style: canvasStyle(live, reduced) })), showFallback && _jsx("span", { style: CENTRED, children: fallback })] }));
}
/** The owl beside a result: one ending, once, then held. */
export function OwlMoment({ moment, label, size = 96, plant = false, fallback = null, className, style }) {
    const canvasRef = useRef(null);
    const [live, setLive] = useState(false);
    const [gaveUp, setGaveUp] = useState(false);
    const [reduced] = useState(prefersReducedMotion);
    useEffect(() => {
        let cancelled = false;
        let drew = false;
        let stop = null;
        // Not drawn in time: the fallback stays for good, and the owl never
        // replays an old result late.
        const deadline = setTimeout(() => {
            if (cancelled || drew)
                return;
            setGaveUp(true);
            stop?.();
            stop = null;
        }, MOMENT_DEADLINE_MS);
        void preloadOwl().then((engine) => {
            const canvas = canvasRef.current;
            if (cancelled || !canvas || !engine)
                return;
            try {
                stop = engine.mountMoment(canvas, {
                    moment,
                    label,
                    plant,
                    reducedMotion: reduced,
                    onLive: (on) => {
                        if (cancelled || !on)
                            return;
                        drew = true;
                        setLive(true);
                    },
                    onDead: () => {
                        if (!cancelled)
                            setGaveUp(true);
                    },
                }).stop;
            }
            catch {
                setGaveUp(true);
            }
        });
        return () => {
            cancelled = true;
            clearTimeout(deadline);
            stop?.();
        };
        // One moment per mount: a host re-keys it for a new result.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const owlOn = live && !gaveUp;
    return (_jsxs("span", { className: className, style: boxStyle(size, style), children: [!gaveUp && (_jsx("canvas", { ref: canvasRef, "aria-hidden": "true", style: canvasStyle(owlOn, reduced) })), !owlOn && _jsx("span", { style: CENTRED, children: fallback })] }));
}
